import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";

// Scroll-driven gold border illumination: quiet at the edges, peaks as the
// element crosses the vertical center of the viewport, then settles. Shared by
// the portfolio venture cards and the division cards.
export const useScrollGlow = (ref) => {
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const glow = useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0]);
  const borderColor = useTransform(glow, (g) => `rgba(180,139,63,${(0.07 + 0.83 * g).toFixed(3)})`);
  const boxShadow = useTransform(
    glow,
    (g) =>
      `0 0 ${(1 + 18 * g).toFixed(1)}px rgba(180,139,63,${(0.16 * g).toFixed(3)}), inset 0 0 ${(10 * g).toFixed(1)}px rgba(180,139,63,${(0.07 * g).toFixed(3)})`
  );
  return { borderColor, boxShadow };
};

// A bordered frame whose perimeter illuminates as it scrolls through center.
export const GlowFrame = ({ to, testid, className = "", innerClassName = "", children }) => {
  const ref = useRef(null);
  const { borderColor, boxShadow } = useScrollGlow(ref);
  const inner = (
    <motion.div ref={ref} style={{ borderColor, boxShadow }} className={`border ${innerClassName}`}>
      {children}
    </motion.div>
  );
  if (to)
    return (
      <Link to={to} data-testid={testid} className={`block group h-full ${className}`}>
        {inner}
      </Link>
    );
  return <div className={className}>{inner}</div>;
};

// Reveal that reliably shows above-the-fold content at mount.
// useInView performs an initial intersection measurement on mount (fires for
// elements already in the viewport WITHOUT needing a scroll event), fixing the
// "content missing until you scroll down and back up" bug.
export const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.12 });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 22 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 }}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};

export const Hairline = ({ className = "" }) => (
  <div className={`hairline ${className}`} aria-hidden="true" />
);

// Section title with flanking gold hairlines
export const SectionTitle = ({ children, tone = "gold", testid }) => (
  <div className="flex items-center justify-center gap-6 w-full" data-testid={testid}>
    <span className="hidden sm:block h-px w-16 md:w-28 bg-gradient-to-r from-transparent to-[var(--gold)]" />
    <h2
      className={`font-display ${tone === "gold" ? "gold-text" : "text-[var(--bone)]"} text-2xl md:text-4xl tracking-wide2 uppercase text-center`}
    >
      {children}
    </h2>
    <span className="hidden sm:block h-px w-16 md:w-28 bg-gradient-to-l from-transparent to-[var(--gold)]" />
  </div>
);

// User-intent light-sweep for primary CTAs: the sweep fires on actual press/tap
// (pointerdown), never automatically on mount or viewport entry. Navigation/submit
// is never intercepted — we only toggle a class and let the action proceed.
export const useSweep = () => {
  const ref = useRef(null);
  const onPointerDown = () => {
    const el = ref.current;
    if (!el) return;
    el.classList.remove("is-sweeping");
    void el.offsetWidth; // reflow so a rapid re-tap restarts the animation
    el.classList.add("is-sweeping");
  };
  const onAnimationEnd = () => {
    if (ref.current) ref.current.classList.remove("is-sweeping");
  };
  return { ref, onPointerDown, onAnimationEnd };
};

export const PillButton = ({ to, href, children, testid, external = false, className = "" }) => {
  const sweep = useSweep();
  const shared = {
    ...sweep,
    "data-testid": testid,
    className: `jmh-cta ${className}`,
  };
  if (href)
    return (
      <a href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} {...shared}>
        {children}
      </a>
    );
  return (
    <Link to={to} {...shared}>
      {children}
    </Link>
  );
};

// Faithful typographic reconstruction of the JMH logotype (placeholder for original vector logo)
export const HoldingsWordmark = ({ size = "md", className = "", widthClass = "" }) => {
  const w = widthClass || (size === "lg" ? "max-w-md md:max-w-lg" : size === "sm" ? "max-w-[200px]" : "max-w-xs md:max-w-sm");
  return (
    <img src="/brand/holdings-official.png" alt="Jennae Michelle Holdings" className={`mx-auto w-full ${w} ${className}`} />
  );
};

export const StewardWordmark = ({ className = "" }) => (
  <div className={`text-center ${className}`}>
    <div className="font-display text-3xl md:text-5xl tracking-wide2 uppercase leading-none">
      <span className="gold-text">Steward </span>
      <span className="silver-text">&amp;</span>
      <span className="gold-text"> Stone</span>
    </div>
  </div>
);


// Capability status chip — distinguishes current platform capabilities from
// pre-launch ("In development") ones. Never uses "live".
export const StatusBadge = ({ status = "In development", className = "" }) => {
  const current = /current/i.test(status);
  return (
    <span
      data-testid="status-badge"
      className={`inline-block font-display uppercase tracking-wide2 text-[9px] px-2.5 py-1 border ${current ? "border-[rgba(180,139,63,0.55)] text-[var(--gold-lite)]" : "border-[rgba(180,139,63,0.22)] text-[var(--bone)]/60"} ${className}`}
    >
      {status}
    </span>
  );
};
